import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  LoadingGroup,
  LoadingSkeleton,
  Message,
  Messages,
  Table,
  Tag,
  useSimpleTableDataSource,
} from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading } from "capitalrx-components-2.0/lib/typography";

import { useListMedicarePrescriptionPaymentPlanUpcomingInvoicesQuery } from "@/App/api/medicarePrescriptionPaymentPlan";
import { UniformRowColorTable } from "@/App/components";
import { usePlanYear } from "@/App/pages";
import { formatDollars } from "@/App/utils/utils";

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.upcomingPaymentsPanel";

export const UpcomingPaymentsPanel: FC = () => {
  const { t: translate } = useTranslation();
  const planYear = usePlanYear();

  const {
    data: upcomingInvoices,
    isLoading: isLoadingUpcomingInvoices,
    isError: isUpcomingInvoicesError,
  } = useListMedicarePrescriptionPaymentPlanUpcomingInvoicesQuery(planYear);

  const upcomingPaymentsRows = useMemo(() => {
    return (upcomingInvoices?.results || []).map((invoice, index) => {
      return {
        id: index,
        data: {
          ...invoice,
        },
      };
    });
  }, [upcomingInvoices]);

  const upcomingPaymentDataSource = useSimpleTableDataSource(upcomingPaymentsRows);

  return (
    <Stack>
      <Heading>
        <Shelf>
          {translate(`${TRANSLATION_PATH}.estimatedUpcomingPayments`)}
          <Tag type="info" style={{ display: "inline-flex", alignItems: "center" }}>{planYear}</Tag>
          <Shelf.Spacer />
        </Shelf>
      </Heading>

      <LoadingGroup loading={isLoadingUpcomingInvoices}>
        <LoadingGroup.Placeholder
          fallback={
            <Stack spacing="mini">
              <LoadingSkeleton style={{ width: "75px", height: "16px" }} />
              <Shelf spacing="mini">
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <Shelf.Spacer />
              </Shelf>
              <Shelf spacing="mini">
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <Shelf.Spacer />
              </Shelf>
              <Shelf spacing="mini">
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <Shelf.Spacer />
              </Shelf>
            </Stack>
          }
        >
          {!isUpcomingInvoicesError && upcomingPaymentsRows.length > 0 && (
            <Stack>
              <Body>{translate(`${TRANSLATION_PATH}.claimsEffectNotice`)}</Body>
              <Shelf>
                <UniformRowColorTable dataSource={upcomingPaymentDataSource}>
                  <Table.Date name="due_date" initialWidth="span" title={translate(`${TRANSLATION_PATH}.dueDate`)} format="LL/dd/yyyy" />
                  <Table.Custom name="amount_due" initialWidth="span" title={translate(`${TRANSLATION_PATH}.amountDue`)}>
                    {(value) => formatDollars(value as string)}
                  </Table.Custom>
                </UniformRowColorTable>
                <Shelf.Spacer />
              </Shelf>
            </Stack>
          )}

          <Message appearance={Messages.Informational} visible={!isUpcomingInvoicesError && upcomingInvoices?.result_count === "0"}>
            {translate(`${TRANSLATION_PATH}.noAccumulatedBalance`)}
          </Message>

          <Message visible={isUpcomingInvoicesError} appearance={Messages.Error}>
            {translate(`${TRANSLATION_PATH}.noUpcomingPaymentsLoaded`)}
          </Message>
        </LoadingGroup.Placeholder>
      </LoadingGroup>
    </Stack>
  );
};
